'use client';

import { Toaster } from 'sonner';

import { styled } from '@mui/material/styles';

import { varAlpha } from 'src/theme/styles';

import { toasterClasses } from './classes';

// ----------------------------------------------------------------------

export const StyledToaster = styled(Toaster)(({ theme }) => {
  const baseStyles = {
    toastDefault: {
      padding: theme.spacing(1, 1, 1, 1.5),
      boxShadow: theme.customShadows.z8,
      color: theme.vars.palette.background.paper,
      backgroundColor: theme.vars.palette.text.primary,
    },
    toastColor: {
      padding: theme.spacing(1, 1.5),
      boxShadow: theme.customShadows.z8,
      color: theme.vars.palette.text.primary,
      backgroundColor: theme.vars.palette.background.paper,
    },
    toastLoader: {
      padding: theme.spacing(0.5, 1, 0.5, 0.5),
      boxShadow: theme.customShadows.z8,
      color: theme.vars.palette.text.primary,
      backgroundColor: theme.vars.palette.background.paper,
    },
  };

  const loadingStyles = {
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'none',
    transform: 'none',
    overflow: 'hidden',
    alignItems: 'center',
    position: 'relative',
    borderRadius: 'inherit',
    justifyContent: 'center',
    background: theme.vars.palette.background.neutral,
    [`& .${toasterClasses.loadingIcon}`]: {
      zIndex: 9,
      width: 24,
      height: 24,
      borderRadius: '50%',
      animation: 'rotate 3s infinite linear',
      background: `conic-gradient(${varAlpha(theme.vars.palette.text.primaryChannel, 0)}, ${varAlpha(theme.vars.palette.text.disabledChannel, 0.64)})`,
    },
    [toasterClasses.loaderVisible]: { display: 'flex' },
  };

  return {
    width: 380,
    maxWidth: 'calc(100vw - 32px)',
    [`& .${toasterClasses.toast}`]: {
      gap: 12,
      width: '100%',
      minHeight: 52,
      display: 'flex',
      borderRadius: 12,
      alignItems: 'center',
      position: 'relative',
      boxShadow: theme.customShadows.z12,
      border: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
    },
    /*
     * Content
     */
    [`& .${toasterClasses.content}`]: {
      gap: 2,
      flex: '1 1 auto',
      minWidth: 0,
    },
    [`& .${toasterClasses.title}`]: {
      fontSize: theme.typography.subtitle2.fontSize,
      fontWeight: theme.typography.fontWeightSemiBold,
      wordBreak: 'break-word',
    },
    [`& .${toasterClasses.description}`]: {
      ...theme.typography.caption,
      opacity: 0.72,
      wordBreak: 'break-word',
    },
    /*
     * Buttons
     */
    [`& .${toasterClasses.actionButton}, & [data-button="true"], & [data-button]`]: {
      flexShrink: 0,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      padding: theme.spacing(0.6, 1.25),
      borderRadius: 8,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightSemiBold,
      border: 'none',
      backgroundColor: theme.vars.palette.text.primary,
      color: theme.vars.palette.background.paper,
      transition: theme.transitions.create(['opacity', 'background-color']),
      '&:hover': {
        opacity: 0.88,
      },
    },
    [`& .${toasterClasses.cancelButton}, & [data-cancel="true"], & [data-cancel]`]: {
      flexShrink: 0,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      padding: theme.spacing(0.6, 1.25),
      borderRadius: 8,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      border: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.24)}`,
      backgroundColor: 'transparent',
      color: theme.vars.palette.text.secondary,
      transition: theme.transitions.create(['background-color', 'border-color']),
      '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
      },
    },
    [`& .${toasterClasses.closeButton}, & [data-close-button="true"], & [data-close-button]`]: {
      top: 8,
      right: 8,
      left: 'auto !important',
      position: 'absolute',
      width: 22,
      height: 22,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      color: theme.vars.palette.text.secondary,
      backgroundColor: theme.vars.palette.background.paper,
      borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.2),
      borderWidth: 1,
      borderStyle: 'solid',
      cursor: 'pointer',
      transform: 'none',
      zIndex: 2,
      transition: theme.transitions.create(['background-color', 'border-color', 'color']),
      '&:hover': {
        color: theme.vars.palette.text.primary,
        borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.4),
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
      },
    },
    /*
     * Icon
     */
    [`& .${toasterClasses.icon}`]: {
      margin: 0,
      width: 44,
      height: 44,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      borderRadius: 'inherit',
      justifyContent: 'center',
      alignSelf: 'flex-start',
      [`& .${toasterClasses.iconSvg}`]: {
        width: 24,
        height: 24,
        fontSize: 0,
      },
    },

    /*
     * Default
     */
    '@keyframes rotate': { to: { transform: 'rotate(1turn)' } },

    [`& .${toasterClasses.default}`]: {
      ...baseStyles.toastDefault,
      [`&:has(${toasterClasses.closeBtnVisible})`]: {
        [`& .${toasterClasses.content}`]: {
          paddingRight: 24,
        },
      },
      [`&:has(.${toasterClasses.loader})`]: baseStyles.toastLoader,
      [`& .${toasterClasses.loader}`]: loadingStyles,
    },
    /*
     * Error
     */
    [`& .${toasterClasses.error}`]: {
      ...baseStyles.toastColor,
      [`& .${toasterClasses.icon}`]: {
        color: theme.vars.palette.error.main,
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
      },
      [`& .${toasterClasses.actionButton}, & [data-button="true"], & [data-button]`]: {
        backgroundColor: theme.vars.palette.error.main,
        color: theme.vars.palette.error.contrastText,
        '&:hover': {
          backgroundColor: theme.vars.palette.error.dark,
        },
      },
    },
    /*
     * Success
     */
    [`& .${toasterClasses.success}`]: {
      ...baseStyles.toastColor,
      [`& .${toasterClasses.icon}`]: {
        color: theme.vars.palette.success.main,
        backgroundColor: varAlpha(theme.vars.palette.success.mainChannel, 0.08),
      },
      [`& .${toasterClasses.actionButton}, & [data-button="true"], & [data-button]`]: {
        backgroundColor: theme.vars.palette.success.main,
        color: theme.vars.palette.success.contrastText,
        '&:hover': {
          backgroundColor: theme.vars.palette.success.dark,
        },
      },
    },
    /*
     * Warning
     */
    [`& .${toasterClasses.warning}`]: {
      ...baseStyles.toastColor,
      [`& .${toasterClasses.icon}`]: {
        color: theme.vars.palette.warning.main,
        backgroundColor: varAlpha(theme.vars.palette.warning.mainChannel, 0.08),
      },
      [`& .${toasterClasses.actionButton}, & [data-button="true"], & [data-button]`]: {
        backgroundColor: theme.vars.palette.warning.main,
        color: theme.vars.palette.warning.contrastText,
        '&:hover': {
          backgroundColor: theme.vars.palette.warning.dark,
        },
      },
    },
    /*
     * Info
     */
    [`& .${toasterClasses.info}`]: {
      ...baseStyles.toastColor,
      [`& .${toasterClasses.icon}`]: {
        color: theme.vars.palette.info.main,
        backgroundColor: varAlpha(theme.vars.palette.info.mainChannel, 0.08),
      },
      [`& .${toasterClasses.actionButton}, & [data-button="true"], & [data-button]`]: {
        backgroundColor: theme.vars.palette.info.main,
        color: theme.vars.palette.info.contrastText,
        '&:hover': {
          backgroundColor: theme.vars.palette.info.dark,
        },
      },
    },
  };
});
